import React, { useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import userApi from "../../api/user/userApi";
import UserContext from "../../state/UserContext";
import Avatar from "../common/Avatar";
import ModalPopup from "../common/ModalPopup";
import Toast from "../common/Toast";
import { ToastConfig } from "../common/ToastConfig";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const VALID_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/gif"];

interface ProfilePictureModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

function ProfilePictureModal({
  isOpen,
  onOpenChange,
}: ProfilePictureModalProps) {
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [toastConfig, setToastConfig] = useState(ToastConfig.hidden());

  const api = new userApi(auth.user?.access_token);
  const { user } = userContext;

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setPreviewUrl((prev) => {
        if (prev) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [isOpen]);

  const validateFile = (file: File): string | null => {
    if (!VALID_FILE_TYPES.includes(file.type)) {
      return "Invalid file type. Please upload a JPG, PNG, or GIF image.";
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return "File is too large. Maximum size is 5MB.";
    }
    return null;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      setToastConfig(ToastConfig.error("Upload failed", validationError));
      return;
    }

    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleChoosePhoto = () => {
    fileInputRef.current?.click();
  };

  const refreshUserContextAfterProfileChange = (
    onComplete: () => void,
    onFailure: (message: string) => void,
  ) => {
    api.getRefreshedLoggedInUserDetails(
      (updates) => {
        userContext.setUpdatedUserDetails(updates);
        onComplete();
      },
      (error) => {
        onFailure(typeof error === "string" ? error : "Please try again.");
      },
    );
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setToastConfig(
        ToastConfig.error("Upload failed", "Please choose a photo first."),
      );
      return;
    }

    setIsLoading(true);
    api.uploadProfilePicture(
      selectedFile,
      () => {
        refreshUserContextAfterProfileChange(
          () => {
            setIsLoading(false);
            onOpenChange(false);
          },
          (message) => {
            setIsLoading(false);
            setToastConfig(
              ToastConfig.error(
                "Profile updated",
                `Your photo was saved, but the display could not be refreshed. ${message}`,
              ),
            );
          },
        );
      },
      (error) => {
        setIsLoading(false);
        setToastConfig(
          ToastConfig.error(
            "Upload failed",
            typeof error === "string" ? error : "Please try again.",
          ),
        );
      },
    );
  };

  const handleRemove = () => {
    setIsLoading(true);
    api.removeProfilePicture(
      () => {
        refreshUserContextAfterProfileChange(
          () => {
            setIsLoading(false);
            onOpenChange(false);
          },
          (message) => {
            setIsLoading(false);
            setToastConfig(
              ToastConfig.error(
                "Photo removed",
                `Your photo was removed, but the display could not be refreshed. ${message}`,
              ),
            );
          },
        );
      },
      (error) => {
        setIsLoading(false);
        setToastConfig(
          ToastConfig.error(
            "Remove failed",
            typeof error === "string" ? error : "Please try again.",
          ),
        );
      },
    );
  };

  return (
    <>
      <ModalPopup
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        title="Change your profile picture"
        width="TINY"
        primaryButtonText="Upload"
        secondaryButtonText="Cancel"
        onPrimaryButtonClick={handleUpload}
        onSecondaryButtonClick={() => onOpenChange(false)}
        isLoading={isLoading}
        isPrimaryButtonDisabled={!selectedFile || isLoading}
      >
        <div className="flex flex-col items-center gap-4 py-2">
          <p className="text-sm text-gray-600 text-center">
            Choose a JPG, PNG, or GIF image (maximum 5MB), then select Upload.
          </p>
          <p className="text-sm text-gray-600 text-center">
            To use your initials again, remove your current photo.
          </p>

          {previewUrl ? (
            <img
              src={previewUrl}
              alt={user.fullName}
              className="w-20 h-20 rounded-full object-cover shadow-lg"
            />
          ) : (
            <Avatar
              userFullName={user.fullName}
              userInitials={user.initials}
              size={20}
              imageUrl={user.profilePic}
              imagePathWidthSize={400}
            />
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/gif"
            onChange={handleFileSelect}
            className="hidden"
          />

          <button
            type="button"
            onClick={handleChoosePhoto}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
            disabled={isLoading}
          >
            Choose photo
          </button>

          {selectedFile && (
            <p className="text-xs text-gray-500 font-mono truncate max-w-full">
              {selectedFile.name}
            </p>
          )}

          {user.profilePic && !selectedFile && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={isLoading}
              className="text-sm text-red-600 hover:text-red-700 font-medium"
            >
              Remove photo
            </button>
          )}
        </div>
      </ModalPopup>

      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </>
  );
}

export default ProfilePictureModal;
